/**
 * Bauteile fuer die Gebaeude.
 *
 * Alle Formen liegen in einem lokalen System: x laeuft ueber die Breite, z ueber die
 * Tiefe, y nach oben, der Ursprung liegt im Mittelpunkt des Grundrisses auf
 * Gelaendehoehe. Keine Form darf ueber `breite/2` bzw. `tiefe/2` hinausreichen -
 * daran haengt Abnahmekriterium A.
 */
import * as THREE from 'three'

/** Achsparalleler Quader zwischen zwei Hoehen. */
export function quader(
  breite: number,
  tiefe: number,
  von: number,
  bis: number,
): THREE.BufferGeometry {
  const g = new THREE.BoxGeometry(breite, bis - von, tiefe)
  g.translate(0, (von + bis) / 2, 0)
  return g
}

interface Ecke {
  x: number
  y: number
  z: number
}

/**
 * Eine ebene Flaeche aus Punkten, gegen den Uhrzeigersinn von aussen gesehen.
 * Die Texturkoordinaten entstehen durch senkrechte Projektion von oben.
 */
function flaeche(punkte: readonly Ecke[], breite: number, tiefe: number): THREE.BufferGeometry {
  const positionen: number[] = []
  const uvs: number[] = []
  for (let i = 1; i + 1 < punkte.length; i++) {
    for (const p of [punkte[0]!, punkte[i]!, punkte[i + 1]!]) {
      positionen.push(p.x, p.y, p.z)
      uvs.push(p.x / breite + 0.5, p.z / tiefe + 0.5)
    }
  }
  const g = new THREE.BufferGeometry()
  g.setAttribute('position', new THREE.Float32BufferAttribute(positionen, 3))
  g.setAttribute('uv', new THREE.Float32BufferAttribute(uvs, 2))
  g.computeVertexNormals()
  return g
}

/**
 * Walmdach ueber der vollen Grundflaeche. `firstAnteil` 0 ergibt ein Zeltdach,
 * 1 ein Satteldach. Der First laeuft entlang der Breite.
 */
export function walmdach(
  breite: number,
  tiefe: number,
  von: number,
  hoehe: number,
  firstAnteil = 0.5,
): THREE.BufferGeometry {
  const b = breite / 2
  const t = tiefe / 2
  const f = (breite * firstAnteil) / 2
  const o = von + hoehe

  const A = { x: -b, y: von, z: -t }
  const B = { x: b, y: von, z: -t }
  const C = { x: b, y: von, z: t }
  const D = { x: -b, y: von, z: t }
  const E = { x: -f, y: o, z: 0 }
  const F = { x: f, y: o, z: 0 }

  const teile = [
    flaeche([B, A, E, F], breite, tiefe), // vorn
    flaeche([D, C, F, E], breite, tiefe), // hinten
    flaeche([A, D, E], breite, tiefe), // links
    flaeche([C, B, F], breite, tiefe), // rechts
  ]
  return verbinde(teile)
}

/**
 * Tonnendach: ein liegender Halbzylinder ueber der vollen Grundflaeche, First entlang
 * der Breite. Von Hand gebaut statt aus einem gedrehten Zylinder, damit die Hoehe
 * unabhaengig von der Tiefe einstellbar bleibt und nichts ueber den Grundriss ragt.
 */
export function tonnendach(
  breite: number,
  tiefe: number,
  von: number,
  hoehe: number,
): THREE.BufferGeometry {
  const n = 20
  const b = breite / 2
  const bogen = (i: number) => {
    const a = (Math.PI * i) / n
    return { y: von + hoehe * Math.sin(a), z: -(tiefe / 2) * Math.cos(a) }
  }
  const teile: THREE.BufferGeometry[] = []
  for (let i = 0; i < n; i++) {
    const p0 = bogen(i)
    const p1 = bogen(i + 1)
    teile.push(
      flaeche(
        [
          { x: b, y: p0.y, z: p0.z },
          { x: -b, y: p0.y, z: p0.z },
          { x: -b, y: p1.y, z: p1.z },
          { x: b, y: p1.y, z: p1.z },
        ],
        breite,
        tiefe,
      ),
    )
  }
  const links: Ecke[] = []
  const rechts: Ecke[] = []
  for (let i = 0; i <= n; i++) {
    const p = bogen(i)
    links.push({ x: -b, y: p.y, z: p.z })
    rechts.unshift({ x: b, y: p.y, z: p.z })
  }
  teile.push(flaeche(links, breite, tiefe), flaeche(rechts, breite, tiefe))
  return verbinde(teile)
}

/** Kuppel: eine gestauchte Halbkugel, die in den Grundriss passt. */
export function kuppel(radius: number, von: number, hoehe: number): THREE.BufferGeometry {
  const g = new THREE.SphereGeometry(radius, 24, 14, 0, Math.PI * 2, 0, Math.PI / 2)
  g.scale(1, hoehe / radius, 1)
  g.translate(0, von, 0)
  return g
}

/** Zwiebelspitze oder Turmhelm. */
export function spitze(radius: number, von: number, hoehe: number): THREE.BufferGeometry {
  const g = new THREE.ConeGeometry(radius, hoehe, 14)
  g.translate(0, von + hoehe / 2, 0)
  return g
}

/** Fasst mehrere Geometrien zu einer zusammen. */
export function verbinde(teile: readonly THREE.BufferGeometry[]): THREE.BufferGeometry {
  const positionen: number[] = []
  const normalen: number[] = []
  const uvs: number[] = []
  for (const g of teile) {
    const nicht = g.index ? g.toNonIndexed() : g
    const p = nicht.getAttribute('position')
    const n = nicht.getAttribute('normal')
    const u = nicht.getAttribute('uv')
    for (let i = 0; i < p.count; i++) {
      positionen.push(p.getX(i), p.getY(i), p.getZ(i))
      normalen.push(n.getX(i), n.getY(i), n.getZ(i))
      uvs.push(u ? u.getX(i) : 0, u ? u.getY(i) : 0)
    }
    if (nicht !== g) nicht.dispose()
    g.dispose()
  }
  const out = new THREE.BufferGeometry()
  out.setAttribute('position', new THREE.Float32BufferAttribute(positionen, 3))
  out.setAttribute('normal', new THREE.Float32BufferAttribute(normalen, 3))
  out.setAttribute('uv', new THREE.Float32BufferAttribute(uvs, 2))
  out.computeBoundingBox()
  return out
}

/**
 * Eine waagerechte Flaeche aus vier Eckpunkten, Normale nach oben.
 *
 * Die Reihenfolge der Ecken ist nicht beliebig: falsch herum zeigt die Normale nach
 * unten, und die Flaeche wird als Rueckseite verworfen. Sichtbar ist sie dann nicht,
 * und - schlimmer - ein Mausstrahl trifft sie auch nicht mehr. Beides ist in diesem
 * Projekt schon einmal passiert, deshalb gibt es diese Funktion und einen Test dazu.
 *
 * Erwartet werden die Ecken eines Strassenfeldes in der Reihenfolge
 * hinten-innen, vorn-innen, vorn-aussen, hinten-aussen.
 */
export function deckflaeche(
  ecken: readonly { x: number; z: number }[],
  y: number,
): THREE.BufferGeometry {
  const [a, b, c, d] = ecken as [
    { x: number; z: number },
    { x: number; z: number },
    { x: number; z: number },
    { x: number; z: number },
  ]
  const pos = [a, b, c, a, c, d].flatMap((p) => [p.x, y, p.z])
  const g = new THREE.BufferGeometry()
  g.setAttribute('position', new THREE.Float32BufferAttribute(pos, 3))
  g.setAttribute(
    'uv',
    new THREE.Float32BufferAttribute([0, 0, 1, 0, 1, 1, 0, 0, 1, 1, 0, 1], 2),
  )
  g.computeVertexNormals()
  return g
}

/**
 * Ein waagerechtes Band zwischen zwei Punktketten, Normale nach oben.
 * `innen` und `aussen` muessen zum selben Kurvenparameter gehoeren, nicht zum
 * selben Polarwinkel - sonst laeuft das Band schief.
 */
export function deckband(
  innen: readonly { x: number; z: number }[],
  aussen: readonly { x: number; z: number }[],
  y: number,
  uvTeiler = 40,
): THREE.BufferGeometry {
  const pos: number[] = []
  const uv: number[] = []
  const n = Math.min(innen.length, aussen.length)
  for (let i = 0; i + 1 < n; i++) {
    const a = innen[i]!
    const b = innen[i + 1]!
    const c = aussen[i + 1]!
    const d = aussen[i]!
    for (const p of [a, b, c, a, c, d]) {
      pos.push(p.x, y, p.z)
      uv.push(p.x / uvTeiler, p.z / uvTeiler)
    }
  }
  const g = new THREE.BufferGeometry()
  g.setAttribute('position', new THREE.Float32BufferAttribute(pos, 3))
  g.setAttribute('uv', new THREE.Float32BufferAttribute(uv, 2))
  g.computeVertexNormals()
  return g
}
